import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { ActionButton } from "./inventory-ui";
import { toInputDate } from "../utils/inventory.utils";

function getTodayInputDate() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function makeLocalDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export default function ConfirmInboundModal({
  open,
  onClose,
  receipt,
  warehouseName,
  onSubmit,
  saving,
}) {
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([]);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    if (!open || !receipt) {
      setNote("");
      setRows([]);
      setPopup(null);
      return;
    }

    setNote("");
    setPopup(null);
    setRows(
      (receipt.items || []).map((item) => ({
        receiptItemId: item.id || item.receiptItemId || null,
        productId: item.productId,
        productVariantId: item.productVariantId || item.product_variant_id || null,
        productName: item.productName || item.name || "",
        variantName: item.variantName || item.variant_name || item.packLabel || item.pack_label || "",
        sku: item.variantSku || item.variant_sku || item.sku || "",
        qty: Number(item.qty || 0),
        receivedQty: Number(item.qty || 0),
        damagedQty: 0,
        uom: item.uom || "pcs",
        batchNo: item.batchNo || "",
        purchasePrice: item.purchasePrice ?? "",
        purchaseCurrency: item.purchaseCurrency || "INR",
        mfgDate: item.mfgDate ? toInputDate(item.mfgDate) : "",
        expiryDate: item.expiryDate ? toInputDate(item.expiryDate) : "",
      }))
    );
  }, [open, receipt]);

  if (!open || !receipt) return null;

  const showErrorPopup = (message) => {
    setPopup({
      title: "Fix receiving details",
      message,
    });
  };

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const submit = () => {
    const today = makeLocalDate(getTodayInputDate());

    for (const [index, row] of rows.entries()) {
      const rowName = row.productName || `Row ${index + 1}`;
      const qty = Number(row.qty || 0);
      const receivedQty = Number(row.receivedQty || 0);
      const damagedQty = Number(row.damagedQty || 0);
      const mfgDate = makeLocalDate(row.mfgDate);
      const expiryDate = makeLocalDate(row.expiryDate);

      if (receivedQty + damagedQty !== qty) {
        showErrorPopup(`${rowName}: Received + Damaged must equal Qty`);
        return;
      }

      if (!String(row.batchNo || "").trim()) {
        showErrorPopup(`${rowName}: Batch No is required`);
        return;
      }

      const batchNo = String(row.batchNo || "").trim();

      if (batchNo.length < 5) {
        showErrorPopup(`${rowName}: Batch No must be at least 5 characters`);
        return;
      }

      if (!row.mfgDate) {
        showErrorPopup(`${rowName}: MFG Date is required`);
        return;
      }

      if (!row.expiryDate) {
        showErrorPopup(`${rowName}: Expiry Date is required`);
        return;
      }

      if (!mfgDate) {
        showErrorPopup(`${rowName}: MFG Date is invalid`);
        return;
      }

      if (!expiryDate) {
        showErrorPopup(`${rowName}: Expiry Date is invalid`);
        return;
      }

      if (today && mfgDate.getTime() > today.getTime()) {
        showErrorPopup(`${rowName}: MFG Date cannot be a future date`);
        return;
      }

      if (expiryDate.getTime() <= mfgDate.getTime()) {
        showErrorPopup(`${rowName}: Expiry Date must be after MFG Date`);
        return;
      }
    }

    onSubmit({
      warehouseId: receipt.warehouseId,
      note: note.trim() || null,
      items: rows.map((r) => ({
        receiptItemId: r.receiptItemId,
        productId: r.productId,
        productVariantId: r.productVariantId,
        receivedQty: Number(r.receivedQty || 0),
        reservedQty: 0,
        damagedQty: Number(r.damagedQty || 0),
        batchNo: String(r.batchNo || "").trim(),
        mfgDate: r.mfgDate || null,
        expiryDate: r.expiryDate || null,
        uom: r.uom || "pcs",
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/45 p-2 sm:p-4">
      <div className="flex h-[96vh] w-[min(96vw,1700px)] max-w-none flex-col overflow-hidden rounded-[20px] sm:rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#f4d8dc] px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <div className="text-[20px] font-bold text-slate-800 sm:text-[24px]">
              Confirm Inbound Receipt
            </div>
            <div className="mt-1 text-[12px] text-slate-500 sm:text-[13px]">
              Warehouse: <span className="font-semibold text-slate-700">{warehouseName}</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-400 sm:text-[12px]">
              Receipt:{" "}
              {receipt.receiptCode ||
                `INB-${String(receipt.id || "")
                  .replace(/-/g, "")
                  .slice(0, 8)
                  .toUpperCase()}`}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-[12px] border border-[#f2c6cc] p-2 text-slate-500 hover:bg-[#fff7f8]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex flex-1 flex-col gap-5 overflow-hidden p-4 sm:p-6">
          <div>
            <label className="mb-2 block text-[13px] font-semibold text-slate-600">
              Note
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note"
              className="h-[42px] w-full rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            />
          </div>

          <div className="overflow-auto rounded-[16px] border border-[#f3d6db]">
            <table className="w-full min-w-[900px]">
              <thead className="sticky top-0 z-[1] bg-[#fff7f8] text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 sm:text-[12px]">
                <tr>
                  <th className="min-w-[220px] px-4 py-3">Product</th>
                  <th className="min-w-[90px] px-4 py-3">Qty</th>
                  <th className="min-w-[110px] px-4 py-3">Received</th>
                  {/* <th className="min-w-[110px] px-4 py-3">Reserved</th> */}
                  <th className="min-w-[110px] px-4 py-3">Damaged</th>
                  <th className="min-w-[130px] px-4 py-3">Purchase Price</th>
                  <th className="min-w-[110px] px-4 py-3">Currency</th>
                  <th className="min-w-[110px] px-4 py-3">UOM</th>
                  <th className="min-w-[180px] px-4 py-3">Batch No</th>
                  <th className="min-w-[160px] px-4 py-3">Mfg Date</th>
                  <th className="min-w-[160px] px-4 py-3">Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={`${row.productId}-${index}`}
                    className="border-t border-[#f8dde1] text-[13px] text-slate-700"
                  >
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800">
                        {row.productName}
                      </div>
                      <div className="text-[12px] text-slate-400">{row.sku}</div>
                    </td>

                    <td className="px-4 py-4">
                      <input
                        value={row.qty}
                        disabled
                        className="h-[40px] w-full min-w-[80px] rounded-[10px] border border-[#f2c6cc] bg-slate-50 px-3 text-[13px] outline-none"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={row.receivedQty}
                        onChange={(e) =>
                          updateRow(index, { receivedQty: Number(e.target.value) })
                        }
                        className="h-[40px] w-full min-w-[90px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px]"
                      />
                    </td>

                    {/* <td className="px-4 py-4">
                      <input
                        type="number"
                        value={row.reservedQty}
                        onChange={(e) =>
                          updateRow(index, { reservedQty: Number(e.target.value) })
                        }
                        className="h-[40px] w-full min-w-[90px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px]"
                      />
                    </td> */}

                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={row.damagedQty}
                        onChange={(e) =>
                          updateRow(index, { damagedQty: Number(e.target.value) })
                        }
                        className="h-[40px] w-full min-w-[90px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px]"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        value={row.purchasePrice}
                        disabled
                        className="h-[40px] w-full min-w-[120px] rounded-[10px] border border-[#f2c6cc] bg-slate-50 px-3 text-[13px] outline-none"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        value={row.purchaseCurrency || "INR"}
                        disabled
                        className="h-[40px] w-full min-w-[90px] rounded-[10px] border border-[#f2c6cc] bg-slate-50 px-3 text-[13px] outline-none"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        value={row.uom}
                        onChange={(e) => updateRow(index, { uom: e.target.value })}
                        className="h-[40px] w-full min-w-[90px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        value={row.batchNo}
                        minLength={5}
                        onChange={(e) => updateRow(index, { batchNo: e.target.value })}
                        placeholder="Min 5 chars"
                        className="h-[40px] w-full min-w-[160px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        type="date"
                        value={row.mfgDate}
                        max={getTodayInputDate()}
                        onChange={(e) => updateRow(index, { mfgDate: e.target.value })}
                        className="h-[40px] w-full min-w-[150px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <input
                        type="date"
                        value={row.expiryDate}
                        min={row.mfgDate || undefined}
                        onChange={(e) =>
                          updateRow(index, { expiryDate: e.target.value })
                        }
                        className="h-[40px] w-full min-w-[150px] rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sticky bottom-0 flex flex-wrap justify-end gap-3 border-t border-[#f4d8dc] bg-white px-4 py-4 sm:px-6">
            <ActionButton variant="soft" onClick={onClose} disabled={saving}>
              Cancel
            </ActionButton>
            <ActionButton onClick={submit} disabled={saving}>
              {saving ? "Confirming..." : "Confirm Receipt"}
            </ActionButton>
          </div>
        </div>
      </div>

      {popup ? (
  <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4">
    <div className="w-full max-w-md overflow-hidden rounded-[22px] bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-[#f8dde1] px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[#fff0f2] p-2 text-[#d86a78]">
            <AlertTriangle size={22} />
          </div>
          <div>
            <h4 className="text-[17px] font-bold text-slate-800">
              {popup.title || "Action needed"}
            </h4>
            <p className="mt-1 text-[13px] leading-5 text-slate-600">
              {popup.message || "Please check details and try again."}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setPopup(null)}
          className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
        >
          <X size={17} />
        </button>
      </div>

      <div className="flex justify-end border-t border-[#f8dde1] px-5 py-4">
        <button
          type="button"
          onClick={() => setPopup(null)}
          className="rounded-[12px] bg-[#d86a78] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#c95c6a]"
        >
          Okay, I will fix it
        </button>
      </div>
    </div>
  </div>
) : null}
    </div>
  );
}